import React, { useCallback, useMemo, useState } from 'react';
import {
  Popconfirm,
  Form,
  Space,
  DatePicker,
  Col,
  Row,
  notification,
} from 'antd';
import { FormInstance } from 'antd/lib/form';
import { PlusOutlined } from '@ant-design/icons';
import { isNil, sumBy } from 'lodash';
import moment from 'moment';

import { toCurrency } from 'utils/utils';
import { dateToString, shouldUpdateCompare } from 'utils/utils2';
import { useFormList } from 'hooks/useFormList';
import Value from 'components/Value';
import DataGrid from 'components/DataGrid';
import ParListSelect from 'components/ParListSelect';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import Title from 'components/Title';
import DeleteButton from 'components/DeleteButton';
import ButtonOperation from 'components/ButtonOperation';
import InputCurrency from 'components/InputCurrency';
import InputRate from 'components/InputRate';
import TextArea from 'antd/lib/input/TextArea';
import ISave from 'icons/ISave';
import {
  ActionsAssetComponentPaymentSchedule,
  AssetComponentPaymentScheduleRequest,
} from 'services/assets';
import _ from 'lodash';

type TabAssetComponentScheduleProps = {
  form: FormInstance;
  formC: FormInstance;
  showAsModal?: boolean;
  compId?: number;
};

const TabAssetComponentSchedule = (props: TabAssetComponentScheduleProps) => {
  const { form, compId, formC } = props;

  const fieldName = 'compassetschedules';

  const { add, remove } = useFormList(form, fieldName, {
    addAsFirst: true,
    idKey: 'psid',
  });

  if (form.getFieldValue(fieldName) === undefined) {
    form.setFieldsValue({
      [fieldName]: [],
    });
  }

  const [deletedItems, SetdeletedItems] = useState<any>([]);

  const addRow = () => {
    const totalAssetAmount: number = form.getFieldValue('compnetamount');
    const totalPaymentsScheduled: number = form.getFieldValue(
      'assetscheduledamount'
    );

    let assetscheduledamount = totalPaymentsScheduled
      ? totalAssetAmount - totalPaymentsScheduled
      : totalAssetAmount;
    assetscheduledamount = assetscheduledamount < 0 ? 0 : assetscheduledamount;
    const item: AssetComponentPaymentScheduleRequest = {
      psid:Math.round(_.random(10000000)),
      paytype: 'ADVANCE',
      paymentdelaycode: '',
      previsionalduedate: moment() as any,
      paypercentage: assetscheduledamount / totalAssetAmount,
      payamount: assetscheduledamount,
      holdbackpercentage: 0,
      holdbackamount: 0,
      holdbackprevreleasedate: moment() as any,
      holdbackreleasedate: moment() as any,
      dueamount: assetscheduledamount,
      payduedate: moment() as any,
      paydate: moment() as any,
      paystatus: 'INIT',
      paycomment: '',
      action: 'ADD',
      compid: compId!,
      psorder: 0,
    };
    form.setFieldsValue({
      totalHoldBackAmount: 0,
      assetscheduledamount: assetscheduledamount,
    });

    add(item);
    recalculate(0, { payamount: assetscheduledamount });
  };

  const recalculate = useCallback(
    (index: number, updatedValues: any) => {
      const values = form.getFieldValue(fieldName);
      const totalAssetAmount: number = form.getFieldValue('compnetamount');
      let newValues = values.map((item: any, i: any) => {
        if (i !== index) {
          return item;
        }

        const updatedItem = { ...item, ...updatedValues };

        const paypercentage = !isNil(updatedValues?.paypercentage)
          ? updatedItem.paypercentage
          : (updatedItem.payamount / totalAssetAmount) * 100;
        const payamount =
          updatedItem.paypercentage >= 0 && !isNil(updatedValues?.paypercentage)
            ? (updatedItem.paypercentage * totalAssetAmount) / 100
            : updatedItem.payamount;
        //holdback
        const holdbackpercentage =
          updatedItem.paytype === 'HOLDBACK'
            ? 100
            : updatedValues?.holdbackpercentage !== undefined
            ? updatedItem.holdbackpercentage
            : payamount !== 0
            ? (updatedItem.holdbackamount / payamount) * 100
            : 0;
        const holdbackamount = isNil(updatedValues.holdbackamount)
          ? (holdbackpercentage * payamount) / 100
          : updatedItem.holdbackamount;
        const dueamount = payamount - holdbackamount;

        return {
          ...updatedItem,
          payamount,
          paypercentage,
          dueamount,
          holdbackpercentage,
          holdbackamount,
        };
      });

      let assetscheduledamount = sumBy(newValues, 'payamount');
      if (assetscheduledamount > form.getFieldValue('compnetsamount')) {
        let amounttoreduce =
          assetscheduledamount - form.getFieldValue('compnetamount');
        newValues = newValues.map((item: any, i: any) => {
          if (i === index) return item;

          if (item.payamount > amounttoreduce && amounttoreduce > 0) {
            const amount = amounttoreduce;
            amounttoreduce = 0;

            return {
              ...item,
              payamount: item.payamount - amount,
              dueamount: item.dueamount - amount,
              paypercentage:
                ((item.payamount - amount) / totalAssetAmount) * 100,
            };
          } else return { ...item };
        });
        assetscheduledamount = sumBy(newValues, 'payamount');
      }
      const totalHoldBackAmount = sumBy(newValues, 'holdbackamount');
      form.setFieldsValue({
        [fieldName]: newValues,
        assetscheduledamount,
        totalHoldBackAmount,
      });
    },
    [form]
  );

  const handleSaveLinesSchedule = async () => {
    const assetComponentPaymentScheduleRequest: Array<AssetComponentPaymentScheduleRequest> =
      form.getFieldValue(fieldName)?.map((ele: any) => ({
        ...ele,
        holdbackprevreleasedate: dateToString(ele.holdbackprevreleasedate),
        holdbackreleasedate: dateToString(ele.holdbackreleasedate),
        paydate: dateToString(ele.paydate),
        payduedate: dateToString(ele.payduedate),
        previsionalduedate: dateToString(ele.previsionalduedate),
      }));
    if (
      form.getFieldValue('assetscheduledamount') !==
        form.getFieldValue('compnetamount') &&
      assetComponentPaymentScheduleRequest.length !== 0
    ) {
      notification.error({
        message: 'Error validating input',
        description:
          'The total Payment Shcedule and the Total lines are different. please check',
      });
      return;
    }

    const assetComponents = formC.getFieldValue('assetComponents');
    formC.setFieldsValue({
      assetComponents: assetComponents.map((comp: any) =>
        comp.compid === compId
          ? { ...comp, assetcompschedule: assetComponentPaymentScheduleRequest }
          : comp
      ),
    });
    await ActionsAssetComponentPaymentSchedule({
      assetComponentPaymentScheduleRequest:
        assetComponentPaymentScheduleRequest.concat(deletedItems),
    }).then((res) => {
      form.setFieldsValue({ requireSave: false });

      notification.success({ message: res.message });
    });
  };

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        key: 'paytype',
        width: 150,
        fixed: 'left',
        render: (_, record, index) => (
          <Form.Item name={[fieldName, index, 'paytype']}>
            <ParListSelect
              parcode="APTYPE"
              disabled={record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'paymentdelaycode',
        key: 'paymentdelaycode',
        width: 180,
        render: (_, record, index) => (
          <Form.Item
            name={[fieldName, index, 'paymentdelaycode']}
            rules={[{ required: true }]}
          >
            <ParListSelect
              parcode="PAYDELAYIN"
              onChange={(value) => {
                let newDate = moment().add(
                  value.replace('M', '').substr(1, value.length),
                  'days'
                );
                if (value.indexOf('M') > -1) {
                  newDate = moment(newDate).endOf('month');
                }
                recalculate(index, {
                  previsionalduedate: newDate,
                });
              }}
              disabled={record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'previsionalduedate',
        key: 'previsionalduedate',
        width: 130,
        render: (_, record, index) => (
          <Form.Item name={[fieldName, index, 'previsionalduedate']}>
            <DatePicker
              value={moment(record.previsionalduedate)}
              disabled={record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'paypercentage',
        width: 140,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'paypercentage']}>
            <InputRate
              onChange={(value) => recalculate(index, { paypercentage: value })}
              disabled={_record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'payamount',
        width: 140,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'payamount']}>
            <InputCurrency
              currcode={formC.getFieldValue('currcode')}
              onChange={(value) => recalculate(index, { payamount: value })}
              disabled={_record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'holdbackpercentage',
        width: 130,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'holdbackpercentage']}>
            <InputRate
              onChange={(value) =>
                recalculate(index, { holdbackpercentage: value })
              }
              min={0}
              disabled={_record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'holdbackamount',
        width: 130,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'holdbackamount']}>
            <InputCurrency
              currcode={formC.getFieldValue('currcode')}
              onChange={(value) =>
                recalculate(index, { holdbackamount: value })
              }
              disabled={_record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'holdbackprevreleasedate',
        width: 130,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'holdbackprevreleasedate']}>
            <DatePicker
              value={moment(_record.holdbackprevreleasedate)}
              disabled={_record.paystatus === 'PAID'}
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'holdbackreleasedate ',
        key: 'holdbackreleasedate ',
        width: 130,
        render: (_, record, index) => (
          <Form.Item name={[fieldName, index, 'holdbackreleasedate ']}>
            {dateToString(record?.holdbackreleasedate)}
          </Form.Item>
        ),
      },
      {
        comcode: 'dueamount',
        key: 'dueamount',
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'dueamount']}>
            <Value
              formatter={(value) =>
                toCurrency(value, formC.getFieldValue('currcode'))
              }
            />
          </Form.Item>
        ),
        width: 130,
      },
      {
        comcode: 'payduedate',
        key: 'payduedate',
        width: 130,
        render: (_, record, index) => {
          return (
            <Form.Item name={[fieldName, index, 'payduedate']}>
              <DatePicker
                value={moment(record.previsionalduedate)}
                disabled={record.paystatus === 'PAID'}
              />
            </Form.Item>
          );
        },
      },
      {
        comcode: 'paydate',
        key: 'paydate',
        width: 130,
        render: (_, record, index) => (
          <Form.Item name={[fieldName, index, 'paydate']}>
            {dateToString(record.paydate)}
          </Form.Item>
        ),
      },

      {
        comcode: 'paystatus',
        key: 'paystatus',
        width: 150,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'paystatus']}>
            <Value />
          </Form.Item>
        ),
      },
      {
        comcode: 'paycomment',
        key: 'paycomment',
        width: 200,
        render: (_value, _record, index) => (
          <Form.Item name={[fieldName, index, 'paycomment']}>
            <TextArea disabled={_record.paystatus === 'PAID'} />
          </Form.Item>
        ),
      },

      {
        title: 'Actions',
        key: 'actions',
        render: (_value, record, index) => (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => {
              form.setFieldsValue({
                assetscheduledamount:
                  form.getFieldValue('assetscheduledamount') - record.payamount,
                totalHoldBackAmount:
                  form.getFieldValue('totalHoldBackAmount') -
                  record.holdbackamount,
              });
              if (record.action === 'UPDATE') {
                let newItems = deletedItems;
                newItems.push({ ...record, action: 'DELETE' });
                SetdeletedItems(newItems);
              }
              form.setFieldsValue({ requireSave: true });
              remove(record.psid);
            }}
          >
            <DeleteButton onlyIcon />
          </Popconfirm>
        ),
        align: 'center',
        fixed: 'right',
        width: 100,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recalculate]
  );

  const title = (
    <>
      <Col span={16} />
      <Form.Item
        noStyle
        shouldUpdate={shouldUpdateCompare([
          'assetscheduledamount',
          'compnetamount',
          'totalHoldBackAmount',
        ])}
      >
        {({ getFieldValue }) => {
          return (
            <Space size={16}>
              <span style={{ color: '#668cff' }}>
                Total Asset Amount :{' '}
                {toCurrency(
                  getFieldValue('compnetamount'),
                  getFieldValue('currcode')
                )}
              </span>
              <span
                style={{
                  color:
                    getFieldValue('assetscheduledamount') ===
                    getFieldValue('compnetamount')
                      ? 'green'
                      : getFieldValue('assetscheduledamount') >
                        getFieldValue('compnetamount')
                      ? 'red'
                      : 'orange',
                }}
              >
                Total Payments Scheduled:{' '}
                {toCurrency(
                  getFieldValue('assetscheduledamount'),
                  getFieldValue('currcode')
                )}
              </span>
              <span
                style={{
                  color:
                    getFieldValue('assetscheduledamount') ===
                    getFieldValue('compnetamount')
                      ? 'green'
                      : getFieldValue('assetscheduledamount') >
                        getFieldValue('compnetamount')
                      ? 'red'
                      : 'orange',
                }}
              >
                Total HoldBack Scheduled:{' '}
                {toCurrency(
                  getFieldValue('totalHoldBackAmount'),
                  getFieldValue('currcode')
                )}
              </span>
            </Space>
          );
        }}
      </Form.Item>
    </>
  );

  return (
    <div style={{ backgroundColor: '#f7f7f7', padding: 5 }}>
      <Title comcode="PaymentSchedule" level={5}></Title>

      <Form.Item
        noStyle
        shouldUpdate={shouldUpdateCompare([
          'compassetschedules',
          'requireSave',
        ])}
      >
        {({ getFieldValue, setFieldsValue }) => (
          <DataGrid
            title={title}
            columns={columns}
            dataSource={getFieldValue(fieldName)}
            rowKey="psid"
            scroll={{ x: 1200, y: 360 }}
            hideFilters
            extraActions={
              <Row justify="end" gutter={16}>
                {getFieldValue('requireSave') === true && (
                  <Col span={12}>
                    <ButtonOperation
                      icon={<ISave twoToneColor="#eb2f96" />}
                      type="default"
                      onClick={() => {
                        handleSaveLinesSchedule();
                      }}
                      comcode="Save"
                    />
                  </Col>
                )}
                <Col span={12}>
                  <ButtonOperation
                    icon={<PlusOutlined />}
                    type="primary"
                    onClick={() => {
                      addRow();
                      setFieldsValue({ requireSave: true });
                    }}
                    comcode="add"
                  />
                </Col>
              </Row>
            }
          />
        )}
      </Form.Item>
    </div>
  );
};

export default TabAssetComponentSchedule;
